import { Controller } from "@hotwired/stimulus";
import Trix from "trix";
// Register heading levels (H1-H6) at module level, before any Trix editors
// initialise. Trix memoises its block attribute name list on first access,
// so these must be present before the first <trix-editor> connects.
Trix.config.blockAttributes.heading1 = {
  tagName: "h1",
  terminal: true,
  breakOnReturn: true,
  group: false,
};
Trix.config.blockAttributes.heading2 = {
  tagName: "h2",
  terminal: true,
  breakOnReturn: true,
  group: false,
};
Trix.config.blockAttributes.heading3 = {
  tagName: "h3",
  terminal: true,
  breakOnReturn: true,
  group: false,
};
Trix.config.blockAttributes.heading4 = {
  tagName: "h4",
  terminal: true,
  breakOnReturn: true,
  group: false,
};
Trix.config.blockAttributes.heading5 = {
  tagName: "h5",
  terminal: true,
  breakOnReturn: true,
  group: false,
};
Trix.config.blockAttributes.heading6 = {
  tagName: "h6",
  terminal: true,
  breakOnReturn: true,
  group: false,
};

// Allow alignment custom elements through Trix's DOMPurify sanitiser.
// Without this, DOMPurify strips the tags when the editor loads existing
// content, causing alignment to revert to left on re-edit.
Trix.config.dompurify.ADD_TAGS = Trix.config.dompurify.ADD_TAGS || [];
Trix.config.dompurify.ADD_TAGS.push(
  "align-left",
  "align-center",
  "align-right",
  "align-justify",
);

// Allow target attribute on links so DOMPurify preserves it when loading
// existing content that contains target="_blank" or target="_self".
Trix.config.dompurify.ADD_ATTR = Trix.config.dompurify.ADD_ATTR || [];
Trix.config.dompurify.ADD_ATTR.push("target");

// Register text alignment as nestable block attributes so they can coexist
// with terminal block attributes like headings. Alignment wraps the block
// element: <align-center><h2>…</h2></align-center>.
// Mutual exclusivity (only one alignment per block) is enforced manually in
// handleAlignmentClick() rather than via `exclusive: true`, because Trix's
// exclusive flag strips ALL block attributes — including headings.
Trix.config.blockAttributes.alignLeft = {
  tagName: "align-left",
  nestable: true,
};
Trix.config.blockAttributes.alignCenter = {
  tagName: "align-center",
  nestable: true,
};
Trix.config.blockAttributes.alignRight = {
  tagName: "align-right",
  nestable: true,
};
Trix.config.blockAttributes.alignJustify = {
  tagName: "align-justify",
  nestable: true,
};

const HEADING_LEVELS = [
  "heading1",
  "heading2",
  "heading3",
  "heading4",
  "heading5",
  "heading6",
];

const ALIGNMENT_LEVELS = [
  "alignLeft",
  "alignCenter",
  "alignRight",
  "alignJustify",
];

export default class extends Controller {
  static values = { textOnly: { type: Boolean, default: false } };
  static targets = [];

  disconnect() {
    if (this.boundInitializeEditor) {
      this.element.removeEventListener(
        "trix-initialize",
        this.boundInitializeEditor,
      );
    }
    if (this.boundHandleActionInvoke) {
      this.element.removeEventListener(
        "trix-action-invoke",
        this.boundHandleActionInvoke,
      );
    }
    if (this.boundHandleContentChange) {
      this.element.removeEventListener(
        "trix-change",
        this.boundHandleContentChange,
      );
    }
    if (this.boundHandleDialogShow) {
      this.element.removeEventListener(
        "trix-toolbar-dialog-show",
        this.boundHandleDialogShow,
      );
    }
    if (this.boundHandleDialogHide) {
      this.element.removeEventListener(
        "trix-toolbar-dialog-hide",
        this.boundHandleDialogHide,
      );
    }
    if (this.boundHandleSelectionChange) {
      this.element.removeEventListener(
        "trix-selection-change",
        this.boundHandleSelectionChange,
      );
    }
    if (this.form && this.boundInjectLinkTargets) {
      this.form.removeEventListener("submit", this.boundInjectLinkTargets);
    }
  }

  connect() {
    this.youtubePattern =
      /^https:\/\/(([^\.]+\.)?youtube\.com\/watch\?.*?v=|youtu\.be\/)([a-zA-Z0-9_-]{11}).*/;
    this.pdfPattern = /^https?:\/\/.*\.pdf(\?.*)?$/i;
    this.currentEmbed = null;

    // Map of href → target value ("_self" or "_blank") for link targets.
    // Trix re-renders its DOM and strips non-standard attributes, so we
    // store target choices here and inject them into the hidden input on
    // form submit.
    //
    // On a Stimulus disconnect/reconnect cycle (e.g. DOM move during
    // sorting) Trix will have already overwritten the hidden input and
    // defaultValue with re-serialised HTML.  We persist the map on
    // the <trix-editor> element itself via a data attribute so it
    // survives reconnection.
    this.linkTargets = this._restoreBackup("data-link-targets-backup") || {};

    const restoredFromBackup = Object.keys(this.linkTargets).length > 0;

    if (!restoredFromBackup) {
      // Parse link targets from the raw HTML BEFORE Trix initialises,
      // because Trix strips non-standard attributes from <a> tags
      // during its init.
      this.parseLinkTargetsFromRawContent();
      this._persistBackup();
    }

    // Bind handler once to avoid duplicate listeners on repeated dialog opens
    this.boundHandleLinkTargetChange = this.handleLinkTargetChange.bind(this);

    // Set up custom toolbar
    Trix.config.toolbar.getDefaultHTML = this.toolbarDefaultHTML.bind(this);

    // Initialize when Trix editor is ready.
    // Store the bound reference so disconnect() can remove it and
    // prevent duplicate listeners across reconnection cycles.
    this.boundInitializeEditor = this.initializeEditor.bind(this);
    this.element.addEventListener(
      "trix-initialize",
      this.boundInitializeEditor,
    );
  }

  initializeEditor(event) {
    this.editor = event.target.editor;
    this.trixEditor = event.target;
    this.toolbar = event.target.toolbarElement;

    // Update toolbar HTML
    this.updateToolbar();

    // Set up event listeners for dynamically created elements
    this.setupEventListeners();

    // Listen for custom toolbar actions (e.g. horizontal rule)
    this.boundHandleActionInvoke = this.handleActionInvoke.bind(this);
    this.element.addEventListener(
      "trix-action-invoke",
      this.boundHandleActionInvoke,
    );

    // Listen for content changes to auto-embed YouTube URLs
    this.boundHandleContentChange = this.handleContentChange.bind(this);
    this.element.addEventListener("trix-change", this.boundHandleContentChange);

    // Intercept form submission to inject link targets
    this.boundInjectLinkTargets = this.injectLinkTargets.bind(this);
    this.form = this.trixEditor.inputElement?.closest("form");
    if (this.form) {
      this.form.addEventListener("submit", this.boundInjectLinkTargets);
    }

    this.reset();
  }

  updateToolbar() {
    if (this.toolbar) {
      this.toolbar.innerHTML = this.toolbarDefaultHTML();
    }
  }

  setupEventListeners() {
    // Find the href input and embed elements in the toolbar
    this.hrefInput = this.toolbar?.querySelector(
      "[data-trix-input][name='href']",
    );
    this.embedButton = this.toolbar?.querySelector(
      "[data-behavior='embed_url']",
    );

    // Add event listeners
    if (this.hrefInput) {
      this.hrefInput.addEventListener("input", this.handleUrlInput.bind(this));
      this.hrefInput.addEventListener(
        "focusin",
        this.handleUrlInput.bind(this),
      );
    }

    if (this.embedButton) {
      this.embedButton.addEventListener("click", this.embedMedia.bind(this));
    }

    // Heading dropdown
    this.headingSelect = this.toolbar?.querySelector(
      "[data-behavior='heading-select']",
    );
    if (this.headingSelect) {
      this.headingSelect.addEventListener(
        "change",
        this.handleHeadingChange.bind(this),
      );
    }

    // Alignment buttons (custom handlers for mutual exclusivity)
    this.alignmentButtons = this.toolbar?.querySelectorAll(
      "[data-alignment-attribute]",
    );
    if (this.alignmentButtons) {
      this.alignmentButtons.forEach((button) => {
        button.addEventListener("click", this.handleAlignmentClick.bind(this));
      });
    }

    // Link style dropdown (set up when link dialog opens/closes)
    this.boundHandleDialogShow = this.handleDialogShow.bind(this);
    this.element.addEventListener(
      "trix-toolbar-dialog-show",
      this.boundHandleDialogShow,
    );

    this.boundHandleDialogHide = this.handleDialogHide.bind(this);
    this.element.addEventListener(
      "trix-toolbar-dialog-hide",
      this.boundHandleDialogHide,
    );

    // Nesting buttons — only relevant inside lists
    this.nestingButtons = this.toolbar?.querySelectorAll(
      "[data-trix-action='increaseNestingLevel'], [data-trix-action='decreaseNestingLevel']",
    );

    // Update heading dropdown, alignment buttons, and nesting buttons on selection change
    this.boundHandleSelectionChange = () => {
      this.updateHeadingSelect();
      this.updateAlignmentButtons();
      this.updateNestingButtons();
    };
    this.element.addEventListener(
      "trix-selection-change",
      this.boundHandleSelectionChange,
    );
  }

  // --- Custom action handling ---

  handleActionInvoke(event) {
    if (event.actionName === "x-horizontal-rule") {
      event.preventDefault();
      const attachment = new Trix.Attachment({
        content: "<hr>",
        contentType: "vnd.rubyonrails.horizontal-rule.html",
      });
      this.editor.insertAttachment(attachment);
    }
  }

  // --- Heading handling ---

  handleHeadingChange(event) {
    const value = event.target.value;

    // Remove all heading attributes first
    HEADING_LEVELS.forEach((attr) => {
      if (this.editor.attributeIsActive(attr)) {
        this.editor.deactivateAttribute(attr);
      }
    });

    // Apply selected heading (if not "Normal")
    if (value) {
      this.editor.activateAttribute(value);
    }

    this.element.focus();
  }

  updateHeadingSelect() {
    if (!this.headingSelect) return;

    const activeHeading = HEADING_LEVELS.find((attr) =>
      this.editor.attributeIsActive(attr),
    );

    this.headingSelect.value = activeHeading || "";
  }

  // --- Alignment handling ---

  handleAlignmentClick(event) {
    const button = event.currentTarget;
    const attribute = button.dataset.alignmentAttribute;
    if (!attribute) return;

    const isActive = this.editor.attributeIsActive(attribute);

    // Record the active heading so we can restore it after alignment changes.
    // Trix won't add a nestable attribute to a block that already has a
    // terminal attribute (heading), so we remove the heading first, apply
    // alignment, then re-apply the heading. Re-applying a terminal attribute
    // only removes other terminal attrs — it leaves nestable attrs intact.
    const activeHeading = HEADING_LEVELS.find((attr) =>
      this.editor.attributeIsActive(attr),
    );

    if (activeHeading) {
      this.editor.deactivateAttribute(activeHeading);
    }

    // Deactivate any active alignment first (mutual exclusivity)
    ALIGNMENT_LEVELS.forEach((attr) => {
      if (this.editor.attributeIsActive(attr)) {
        this.editor.deactivateAttribute(attr);
      }
    });

    // Toggle: only activate if it wasn't already active
    if (!isActive) {
      this.editor.activateAttribute(attribute);
    }

    // Restore heading
    if (activeHeading) {
      this.editor.activateAttribute(activeHeading);
    }

    this.updateAlignmentButtons();
    this.element.focus();
  }

  updateAlignmentButtons() {
    if (!this.alignmentButtons) return;

    this.alignmentButtons.forEach((button) => {
      const attr = button.dataset.alignmentAttribute;
      button.classList.toggle(
        "trix-active",
        this.editor.attributeIsActive(attr),
      );
    });
  }

  updateNestingButtons() {
    if (!this.nestingButtons) return;

    // Only enable indent/outdent inside bullet or number lists.
    // Alignment is also nestable, so Trix's native state check would
    // incorrectly enable these buttons on aligned non-list blocks.
    const inList =
      this.editor.attributeIsActive("bullet") ||
      this.editor.attributeIsActive("number");

    this.nestingButtons.forEach((button) => {
      button.disabled = !inList;
    });
  }

  // --- Link target handling (form submit injection) ---

  parseLinkTargetsFromRawContent() {
    const rawHtml =
      this.element.defaultValue ||
      (() => {
        const inputId = this.element.getAttribute("input");
        const inputEl = inputId ? document.getElementById(inputId) : null;
        return inputEl?.value;
      })();
    if (!rawHtml) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, "text/html");
    doc.querySelectorAll("a[href][target]").forEach((link) => {
      const target = link.getAttribute("target");
      if (target === "_blank" || target === "_self") {
        this.linkTargets[link.getAttribute("href")] = target;
      }
    });
  }

  handleDialogShow(event) {
    if (event.dialogName === "href") {
      // Capture the href at dialog open so we can detect URL changes
      // and migrate the target mapping when the dialog closes.
      this._dialogOpenHref = this.hrefInput?.value?.trim() || "";

      this.linkTargetSelect = this.toolbar?.querySelector(
        "[data-behavior='link-target-select']",
      );
      if (this.linkTargetSelect) {
        this.linkTargetSelect.removeEventListener(
          "change",
          this.boundHandleLinkTargetChange,
        );
        this.linkTargetSelect.addEventListener(
          "change",
          this.boundHandleLinkTargetChange,
        );

        // Show current target for the active link (default to _self)
        const href = this._dialogOpenHref;
        if (href && this.linkTargets[href]) {
          this.linkTargetSelect.value = this.linkTargets[href];
        } else {
          this.linkTargetSelect.value = "_self";
        }
      }
    }
  }

  handleDialogHide(event) {
    if (event.dialogName !== "href") return;

    const oldHref = this._dialogOpenHref || "";
    const newHref = this.hrefInput?.value?.trim() || "";
    this._dialogOpenHref = null;

    if (!oldHref || oldHref === newHref) return;

    // The user changed the link URL. Migrate any existing target
    // mapping from the old href to the new one so it isn't orphaned
    // and lost on the next form submit.
    if (this.linkTargets[oldHref] && !this.linkTargets[newHref]) {
      this.linkTargets[newHref] = this.linkTargets[oldHref];
      delete this.linkTargets[oldHref];
    }

    this._persistBackup();
  }

  handleLinkTargetChange() {
    const targetValue = this.linkTargetSelect.value;
    const href = this.hrefInput?.value?.trim();
    if (!href) return;

    if (targetValue && targetValue !== "_self") {
      this.linkTargets[href] = targetValue;
    } else {
      // _self is the default — no need to store it
      delete this.linkTargets[href];
    }
    this._persistBackup();
  }

  injectLinkTargets() {
    // Called on form submit — inject target attributes into links in the
    // hidden input. We use targeted regex replacement instead of
    // DOMParser + innerHTML to avoid reserialising the entire HTML,
    // which could corrupt ActionText attachment markup or reorder attributes.
    const inputEl = this.trixEditor.inputElement;
    if (!inputEl?.value) return;

    if (Object.keys(this.linkTargets).length === 0) return;

    // Match <a ...> tags, capturing everything inside the tag
    inputEl.value = inputEl.value.replace(/<a\s([^>]*?)>/gi, (match, attrs) => {
      // Extract the href value
      const hrefMatch = attrs.match(/href="([^"]*)"/);
      if (!hrefMatch) return match;

      const href = hrefMatch[1];
      let cleanedAttrs = attrs;

      // --- Inject link target attribute ---
      const targetValue = this.linkTargets[href];
      if (targetValue) {
        // Replace existing target or append
        if (/target="/.test(cleanedAttrs)) {
          cleanedAttrs = cleanedAttrs.replace(
            /target="[^"]*"/,
            `target="${targetValue}"`,
          );
        } else {
          cleanedAttrs += ` target="${targetValue}"`;
        }
      }

      return `<a ${cleanedAttrs}>`;
    });
  }

  // --- Backup helpers (survive Stimulus disconnect/reconnect) ---

  _persistBackup() {
    this.element.setAttribute(
      "data-link-targets-backup",
      JSON.stringify(this.linkTargets),
    );
  }

  _restoreBackup(attr) {
    const raw = this.element.getAttribute(attr);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return Object.keys(parsed).length > 0 ? parsed : null;
    } catch {
      return null;
    }
  }

  // --- Embed handling ---

  handleUrlInput(event) {
    const value = event.target.value.trim();
    const youtubeMatches = value.match(this.youtubePattern);
    const pdfMatches = value.match(this.pdfPattern);

    if (youtubeMatches != null) {
      this.fetchEmbedData(youtubeMatches[3], "dialog");
    } else if (pdfMatches != null) {
      this.fetchEmbedData(value, "dialog");
    } else {
      this.reset();
    }
  }

  handleContentChange() {
    // Small delay to ensure content is fully updated
    setTimeout(() => {
      this.scanAndReplaceEmbedUrls();
    }, 100);
  }

  scanAndReplaceEmbedUrls() {
    const document = this.editor.getDocument();
    const blocks = document.getBlocks();

    blocks.forEach((block, blockIndex) => {
      const text = block.toString();
      const youtubeMatches = text.match(this.youtubePattern);
      const pdfMatches = text.match(this.pdfPattern);

      if (youtubeMatches) {
        const videoId = youtubeMatches[3];
        const url = youtubeMatches[0];

        // Find the position of the URL in the block
        const urlStart = text.indexOf(url);
        if (urlStart !== -1) {
          this.replaceUrlWithEmbed(blockIndex, urlStart, url.length, videoId);
        }
      } else if (pdfMatches) {
        const url = pdfMatches[0];

        // Find the position of the URL in the block
        const urlStart = text.indexOf(url);
        if (urlStart !== -1) {
          this.replaceUrlWithEmbed(blockIndex, urlStart, url.length, url);
        }
      }
    });
  }

  async replaceUrlWithEmbed(blockIndex, urlStart, urlLength, embedId) {
    try {
      const response = await fetch(
        `/embeds?url=${encodeURIComponent(embedId)}`,
      );
      const embed = await response.text();

      // Calculate the position in the document
      const document = this.editor.getDocument();
      let position = 0;

      // Add up lengths of all blocks before the target block
      for (let i = 0; i < blockIndex; i++) {
        position += document.getBlockAtIndex(i).getLength();
      }

      // Add the position within the block
      position += urlStart;

      // Select and replace the URL text
      this.editor.setSelectedRange([position, position + urlLength]);

      // Insert the embed attachment
      const attachment = new Trix.Attachment(JSON.parse(embed));
      this.editor.insertAttachment(attachment);
    } catch (error) {
      console.error("Failed to fetch embed data for auto-embed:", error);
    }
  }

  async fetchEmbedData(embedId, source = "dialog") {
    const url = `/embeds?url=${encodeURIComponent(embedId)}`;

    try {
      const response = await fetch(url);
      const embed = await response.text();

      if (source === "dialog") {
        this.showEmbedOption(embed);
      }
    } catch (error) {
      console.error("Failed to fetch embed data:", error);
      if (source === "dialog") {
        this.reset();
      }
    }
  }

  embedMedia() {
    if (this.currentEmbed == null) {
      return;
    }

    const attachment = new Trix.Attachment(JSON.parse(this.currentEmbed));
    this.editor.insertAttachment(attachment);
    this.element.focus();
  }

  showEmbedOption(embed) {
    this.currentEmbed = embed;
    if (this.embedButton) {
      this.embedButton.style.display = "block";
    }
  }

  reset() {
    if (this.embedButton) {
      this.embedButton.style.display = "none";
    }
    this.currentEmbed = null;
  }

  // --- Toolbar HTML ---

  toolbarDefaultHTML() {
    const { lang } = Trix.config;
    const textOnly = this.textOnlyValue;

    const linkTargetOptions = [
      { value: "_self", label: "Same tab" },
      { value: "_blank", label: "New tab" },
    ]
      .map((opt) => `<option value='${opt.value}'>${opt.label}</option>`)
      .join("");

    const blockToolsGroup = textOnly
      ? ""
      : `
        <span class='trix-button-group trix-button-group--block-tools' data-trix-button-group='block-tools'>
          <select class='trix-heading-select' data-behavior='heading-select' title='Heading Type' aria-label='Heading Type' tabindex='-1'>
            <option value=''>Normal</option>
            <option value='heading2'>Heading 2</option>
            <option value='heading3'>Heading 3</option>
            <option value='heading4'>Heading 4</option>
            <option value='heading5'>Heading 5</option>
            <option value='heading6'>Heading 6</option>
            <option value='heading1'>Heading 1</option>
          </select>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-quote' data-trix-attribute='quote' title='${lang.quote}' tabindex='-1'>${lang.quote}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-bullet-list' data-trix-attribute='bullet' title='${lang.bullets}' tabindex='-1'>${lang.bullets}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-number-list' data-trix-attribute='number' title='${lang.numbers}' tabindex='-1'>${lang.numbers}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-decrease-nesting-level' data-trix-action='decreaseNestingLevel' title='${lang.outdent}' tabindex='-1'>${lang.outdent}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-increase-nesting-level' data-trix-action='increaseNestingLevel' title='${lang.indent}' tabindex='-1'>${lang.indent}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-horizontal-rule' data-trix-action='x-horizontal-rule' title='Horizontal Rule' tabindex='-1'>Horizontal Rule</button>
        </span>`;

    const alignmentToolsGroup = textOnly
      ? ""
      : `
        <span class='trix-button-group trix-button-group--alignment-tools' data-trix-button-group='alignment-tools'>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-align-left' data-alignment-attribute='alignLeft' title='Align Left' tabindex='-1'>Align Left</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-align-center' data-alignment-attribute='alignCenter' title='Align Centre' tabindex='-1'>Align Centre</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-align-right' data-alignment-attribute='alignRight' title='Align Right' tabindex='-1'>Align Right</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-align-justify' data-alignment-attribute='alignJustify' title='Justify' tabindex='-1'>Justify</button>
        </span>`;

    const fileToolsGroup = textOnly
      ? ""
      : `
        <span class='trix-button-group trix-button-group--file-tools' data-trix-button-group='file-tools'>
          <button type="button" class="trix-button trix-button--icon trix-button--icon-file" data-trix-action="attachFiles" title="${lang.attachFiles}" tabindex="-1"><i class="fas fa-image"></i></button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-attach' data-trix-action='attachFiles' title='${lang.attachFiles}' tabindex='-1'>${lang.attachFiles}</button>
        </span>`;

    const embedMediaButton = textOnly
      ? ""
      : `<input type='button' class='btn btn-secondary btn-small mr-1' data-behavior='embed_url' value='Embed Media'>`;

    return `
      <div class='trix-button-row'>
        <span class='trix-button-group trix-button-group--text-tools' data-trix-button-group='text-tools'>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-bold' data-trix-attribute='bold' data-trix-key='b' title='${lang.bold}' tabindex='-1'>${lang.bold}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-italic' data-trix-attribute='italic' data-trix-key='i' title='${lang.italic}' tabindex='-1'>${lang.italic}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-strike' data-trix-attribute='strike' title='${lang.strike}' tabindex='-1'>${lang.strike}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-link' data-trix-attribute='href' data-trix-action='link' data-trix-key='k' title='${lang.link}' tabindex='-1'>${lang.link}</button>
        </span>${blockToolsGroup}${alignmentToolsGroup}${fileToolsGroup}
        <span class='trix-button-group-spacer'></span>
        <span class='trix-button-group trix-button-group--history-tools' data-trix-button-group='history-tools'>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-undo' data-trix-action='undo' data-trix-key='z' title='${lang.undo}' tabindex='-1'>${lang.undo}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-redo' data-trix-action='redo' data-trix-key='shift+z' title='${lang.redo}' tabindex='-1'>${lang.redo}</button>
        </span>
      </div>
      <div class='trix-dialogs' data-trix-dialogs>
        <div class='trix-dialog trix-dialog--link' data-trix-dialog='href' data-trix-dialog-attribute='href'>
          <div class='trix-dialog__link-fields'>
            <input type='text' name='href' class='trix-input trix-input--dialog' placeholder='${lang.urlPlaceholder}' aria-label='${lang.url}' data-trix-input>
            <div>
              <label class='trix-link-label' for='trix-link-target-select'>Target:</label>
              <select id='trix-link-target-select' class='trix-link-select' data-behavior='link-target-select' title='Link target' tabindex='-1'>
                ${linkTargetOptions}
              </select>
            </div>
            <div class='d-flex gap-1'>
              ${embedMediaButton}
              <input type='button' class='btn btn-secondary btn-small mr-1' value='${lang.link}' data-trix-method='setAttribute'>
              <input type='button' class='btn btn-tertiary outline btn-small' value='${lang.unlink}' data-trix-method='removeAttribute'>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
